// src/services/orderService.js
import { database } from "../../../firebase";
import { ref, push, set, onValue, update, off } from "firebase/database";

/**
 * Mengambil semua konfigurasi alur dari Firebase.
 * @param {function} callback - Fungsi yang akan dipanggil dengan data konfigurasi alur.
 * @param {function} errorCallback - Fungsi yang akan dipanggil jika terjadi error.
 * @returns {function} Fungsi unsubscribe untuk menghentikan listener.
 */
export const fetchFlowConfigurations = (callback, errorCallback) => {
    const flowsRef = ref(database, 'db_config/db_scm/tb_flow_order_barang_expand');
    const unsubscribe = onValue(flowsRef, (snapshot) => {
        const flowsData = [];
        snapshot.forEach((childSnapshot) => {
            flowsData.push({ id: childSnapshot.key, ...childSnapshot.val() });
        });
        callback(flowsData);
    }, errorCallback);
    return () => off(flowsRef, 'value', unsubscribe);
};

/**
 * Mengambil ID jabatan admin (Section Head) dari Firebase.
 * @param {function} callback - Fungsi yang akan dipanggil dengan ID jabatan admin.
 * @param {function} errorCallback - Fungsi yang akan dipanggil jika terjadi error.
 * @returns {function} Fungsi unsubscribe untuk menghentikan listener.
 */
export const fetchAdminJabatanId = (callback, errorCallback) => {
    const jabatanRef = ref(database, 'db_config/db_wfms/tb_jabatan');
    const unsubscribe = onValue(jabatanRef, (snapshot) => {
        let foundAdminId = null;
        snapshot.forEach((childSnapshot) => {
            const jabatanData = childSnapshot.val();
            if (jabatanData.nama_jabatan && jabatanData.nama_jabatan.toLowerCase() === 'section head') {
                foundAdminId = childSnapshot.key;
            }
        });
        callback(foundAdminId);
    }, errorCallback);
    return () => off(jabatanRef, 'value', unsubscribe);
};

/**
 * Mengambil ID role "Developer" dari Firebase.
 * @param {function} callback - Fungsi yang akan dipanggil dengan ID role.
 * @param {function} errorCallback - Fungsi yang akan dipanggil jika terjadi error.
 * @returns {function} Fungsi unsubscribe untuk menghentikan listener.
 */
export const fetchDeveloperRoleId = (callback, errorCallback) => {
    const rolesRef = ref(database, 'db_config/db_wfms/tb_role');
    const unsubscribe = onValue(rolesRef, (snapshot) => {
        let foundRoleId = null;
        snapshot.forEach((childSnapshot) => {
            const roleData = childSnapshot.val();
            if (roleData.nama_role && roleData.nama_role === 'Developer') {
                foundRoleId = childSnapshot.key;
            }
        });
        callback(foundRoleId);
    }, errorCallback);
    return () => off(rolesRef, 'value', unsubscribe);
};

/**
 * Mengambil ID role "Prcn. & Inv. Material Pack PH" dari Firebase.
 * @param {function} callback - Fungsi yang akan dipanggil dengan ID role.
 * @param {function} errorCallback - Fungsi yang akan dipanggil jika terjadi error.
 * @returns {function} Fungsi unsubscribe untuk menghentikan listener.
 */
export const fetchPrcnInvMaterialPackPHRoleId = (callback, errorCallback) => {
    const rolesRef = ref(database, 'db_config/db_wfms/tb_role');
    const unsubscribe = onValue(rolesRef, (snapshot) => {
        let foundPrcnInvRoleId = null;
        snapshot.forEach((childSnapshot) => {
            const roleData = childSnapshot.val();
            if (roleData.nama_role && roleData.nama_role === 'Prcn. & Inv. Material Pack PH') {
                foundPrcnInvRoleId = childSnapshot.key;
            }
        });
        callback(foundPrcnInvRoleId);
    }, errorCallback);
    return () => off(rolesRef, 'value', unsubscribe);
};

/**
 * Mengambil ID role "Inv Management FreshPine & Other" dari Firebase.
 * @param {function} callback - Fungsi yang akan dipanggil dengan ID role.
 * @param {function} errorCallback - Fungsi yang akan dipanggil jika terjadi error.
 * @returns {function} Fungsi unsubscribe untuk menghentikan listener.
 */
export const fetchInvManagementFreshPineOtherRoleId = (callback, errorCallback) => {
    const rolesRef = ref(database, 'db_config/db_wfms/tb_role');
    const unsubscribe = onValue(rolesRef, (snapshot) => {
        let foundRoleId = null;
        snapshot.forEach((childSnapshot) => {
            const roleData = childSnapshot.val();
            if (roleData.nama_role && roleData.nama_role === 'Inv Management FreshPine & Other') {
                foundRoleId = childSnapshot.key;
            }
        });
        callback(foundRoleId);
    }, errorCallback);
    return () => off(rolesRef, 'value', unsubscribe);
};

/**
 * Mengambil ID role "Cost Analyst Fresh Pine & Othrs" dari Firebase.
 * @param {function} callback - Fungsi yang akan dipanggil dengan ID role.
 * @param {function} errorCallback - Fungsi yang akan dipanggil jika terjadi error.
 * @returns {function} Fungsi unsubscribe untuk menghentikan listener.
 */
export const fetchCostAnalystFreshPineOtherRoleId = (callback, errorCallback) => {
    const rolesRef = ref(database, 'db_config/db_wfms/tb_role');
    const unsubscribe = onValue(rolesRef, (snapshot) => {
        let foundRoleId = null;
        snapshot.forEach((childSnapshot) => {
            const roleData = childSnapshot.val();
            if (roleData.nama_role && roleData.nama_role === 'Cost Analyst Fresh Pine & Othrs') {
                foundRoleId = childSnapshot.key;
            }
        });
        callback(foundRoleId);
    }, errorCallback);
    return () => off(rolesRef, 'value', unsubscribe);
};

/**
 * Mengambil daftar Unit of Measure (UoM) dari Firebase.
 * @param {function} callback - Fungsi yang akan dipanggil dengan daftar UoM.
 * @param {function} errorCallback - Fungsi yang akan dipanggil jika terjadi error.
 * @returns {function} Fungsi unsubscribe untuk menghentikan listener.
 */
export const fetchUomList = (callback, errorCallback) => {
    const uomRef = ref(database, 'db_warehouse/db_ppic/db_config/tb_uom');
    const unsubscribe = onValue(uomRef, (snapshot) => {
        const uomData = [];
        snapshot.forEach((childSnapshot) => {
            uomData.push({ id: childSnapshot.key, ...childSnapshot.val() });
        });
        callback(uomData);
    }, errorCallback);
    return () => off(uomRef, 'value', unsubscribe);
};

/**
 * Mengambil semua order dari Firebase.
 * @param {function} callback - Fungsi yang akan dipanggil dengan data order.
 * @param {function} errorCallback - Fungsi yang akan dipanggil jika terjadi error.
 * @returns {function} Fungsi unsubscribe untuk menghentikan listener.
 */
export const fetchOrders = (callback, errorCallback) => {
    const ordersRef = ref(database, 'db_scm/tb_order_barang_expand');
    const unsubscribe = onValue(ordersRef, (snapshot) => {
        const allOrdersData = [];
        snapshot.forEach((childSnapshot) => {
            allOrdersData.push({ id: childSnapshot.key, ...childSnapshot.val() });
        });
        callback(allOrdersData);
    }, errorCallback);
    return () => off(ordersRef, 'value', unsubscribe);
};

/**
 * Mengajukan order baru ke Firebase.
 * @param {object} orderData - Data order yang akan diajukan.
 * @returns {Promise<void>}
 */
export const submitNewOrder = async (orderData) => {
    const newOrderRef = push(ref(database, 'db_scm/tb_order_barang_expand'));
    await set(newOrderRef, {
        ...orderData,
        order_title: orderData.order_title || '', // Pastikan disertakan
        total_amount: orderData.total_amount || 0 // Pastikan disertakan
    });
};

/**
 * Mengajukan ulang atau mengedit order yang sudah ada di Firebase.
 * @param {string} orderId - ID order yang akan diedit.
 * @param {object} updatedData - Data yang akan diperbarui untuk order.
 * @returns {Promise<void>}
 */
export const resubmitOrder = async (orderId, updatedData) => {
    const orderRef = ref(database, `db_scm/tb_order_barang_expand/${orderId}`);
    await update(orderRef, {
        ...updatedData,
        order_title: updatedData.order_title || '',
        total_amount: updatedData.total_amount || 0,
        // Pastikan approval_history disertakan
        approval_history: updatedData.approval_history || []
    });
};

/**
 * Memperbarui status order di Firebase (menyetujui/menolak).
 * @param {string} orderId - ID order yang akan diperbarui.
 * @param {object} updatedFields - Objek yang berisi field yang akan diperbarui (status, current_approval_step_index, approval_history).
 * @returns {Promise<void>}
 */
export const updateOrderStatus = async (orderId, updatedFields) => {
    const orderRef = ref(database, `db_scm/tb_order_barang_expand/${orderId}`);
    await update(orderRef, updatedFields);
};
